/**
 * The route raster the real robot's driver sees — rendered from the sim to
 * the MEASURED spec of the robot's own publisher (ADR-076).
 *
 * Source of truth: `Xavier_ROS2/route_planning/scripts/routed_map_publisher.py`,
 * the node that publishes `/routed_map` on Bulldog04, checked against seven
 * bags from five recording sets. What it does, in pixels:
 *
 *   - draws EVERY way of the .osm the planner routes on as a white
 *     matplotlib line — linewidth 1 pt at 406.4 dpi = 5.64 px — whatever the
 *     real road's width (the OSM way is a centreline; the white is a stroke
 *     along it, not the carriageway filled);
 *   - draws the whole global route in red, the same width, on top; then the
 *     first 20 m of the route in green (#008000) and the last 20 m in blue
 *     (halves of the route when it is under 40 m). The red is NOT trimmed as
 *     the robot advances: the leg behind the robot stays red;
 *   - crops a 50 m window around the robot, rotates it heading-up, crops
 *     160×160 → 0.3125 m/px with the robot at the image CENTRE (25 m ahead,
 *     behind and to each side; the driver keeps the central 128×128 = ±20 m);
 *   - publishes 8UC3 BGR at 10 Hz.
 *
 * Measured, not assumed (DRIVER_IN_THE_LOOP_VI.md §1): with the robot at the
 * centre, its future odometry lies 0.7 px from the drawn route; at the bottom
 * (v1's guess) 2.5 px. White lines are 5–6 px wide in every bag; the blue
 * segment is 64–67 px long — 20 m at 0.3125 m/px. The ADR-075 raster had the
 * robot at the bottom, a 0.25 m/px scale, the road filled to its width and
 * the red cut at the next turn; every one of those was wrong.
 *
 * Here the "ways" are the sim's road pieces (band centrelines, roundabout
 * circles) and the "route" is the delivery path. Supersampled and
 * box-filtered for matplotlib's antialiasing. No DOM, no Node: it runs in the
 * browser, in the capture pipeline and under Vitest alike. Colours are RGB
 * until `bgr()`, which produces the robot's byte order.
 */

import type { Vec2 } from "../core/vec";
import type { RoadNetwork } from "./network";
import type { DeliveryPath } from "./path";

/** The real raster, as measured. Written into every clip's metadata. */
export const ROUTED_MAP_SPEC = Object.freeze({
  size_px: 160,
  window_m: 50,
  /** 50 m / 160 px. */
  metres_per_px: 0.3125,
  /** matplotlib linewidth 1 pt at 160 / (50 × 39.37 / 5000) = 406.4 dpi → 5.644 px → metres. */
  line_width_m: 1.7639,
  /** Continuous pixel coordinates of the robot: the image centre. */
  robot_px: [80, 80] as const,
  /** Green head / blue tail of the route (DISTANCE_MIN_START_END). */
  start_end_m: 20,
  /** Routes shorter than this are split in halves instead (DISTANCE_MIN_PATH). */
  min_path_m: 40,
  publish_hz: 10,
  encoding: "8UC3 (BGR)",
  colours: {
    black: "background",
    white: "every way of the map, stroked along its centreline",
    red: "the whole route, ahead and behind",
    green: "first 20 m of the route (#008000)",
    blue: "last 20 m of the route",
  },
});

export interface RoutedMapOptions {
  /** Image side in pixels. Default 160. */
  size?: number;
  /** Metres per pixel. Default 0.3125. */
  mpp?: number;
  /** Stroke width of every line, in metres. Default 1.7639 (1 pt at the publisher's dpi). */
  lineWidthM?: number;
  /** Supersampling factor for antialiasing. Default 4. 1 = hard edges. */
  ss?: number;
  /** Length of the green head and blue tail, m. Default 20. */
  startEndM?: number;
  /** Routes shorter than this are split in halves. Default 40. */
  minPathM?: number;
}

export interface RoutedMap {
  size: number;
  mpp: number;
  /** RGB, row-major, top row first: `size * size * 3` bytes. */
  rgb: Uint8Array;
}

// Ink indices in the supersampled layer; later strokes overwrite earlier ones,
// which is matplotlib's z-order for the publisher's plot calls.
const INK_NONE = 0;
const INK_WHITE = 1;
const INK_RED = 2;
const INK_GREEN = 3;
const INK_BLUE = 4;
/** matplotlib named colours: "green" is #008000, not #00ff00. */
const INK_RGB: ReadonlyArray<readonly [number, number, number]> = [
  [0, 0, 0],
  [255, 255, 255],
  [255, 0, 0],
  [0, 128, 0],
  [0, 0, 255],
];

/** Stroke one segment as a capsule (round caps, so joins are round too). */
function strokeSegment(
  ink: Uint8Array,
  W: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  r: number,
  value: number,
): void {
  const minX = Math.max(0, Math.floor(Math.min(ax, bx) - r));
  const maxX = Math.min(W - 1, Math.ceil(Math.max(ax, bx) + r));
  const minY = Math.max(0, Math.floor(Math.min(ay, by) - r));
  const maxY = Math.min(W - 1, Math.ceil(Math.max(ay, by) + r));
  // Segments outside the window clip to an empty box and cost nothing.
  if (minX > maxX || minY > maxY) return;
  const vx = bx - ax;
  const vy = by - ay;
  const vv = vx * vx + vy * vy;
  const r2 = r * r;
  for (let y = minY; y <= maxY; y++) {
    const py = y + 0.5;
    const row = y * W;
    for (let x = minX; x <= maxX; x++) {
      const px = x + 0.5;
      let t = vv < 1e-12 ? 0 : ((px - ax) * vx + (py - ay) * vy) / vv;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      const dx = px - (ax + vx * t);
      const dy = py - (ay + vy * t);
      if (dx * dx + dy * dy <= r2) ink[row + x] = value;
    }
  }
}

/**
 * Render the routed map for a robot pose, to the real raster's spec. The
 * whole route is drawn, ahead and behind; nothing depends on how far along
 * it the robot is, exactly as on the robot.
 */
export function renderRoutedMap(
  net: RoadNetwork,
  path: DeliveryPath,
  pose: { x: number; z: number; heading: number },
  opts: RoutedMapOptions = {},
): RoutedMap {
  const size = opts.size ?? ROUTED_MAP_SPEC.size_px;
  const mpp = opts.mpp ?? ROUTED_MAP_SPEC.metres_per_px;
  const ss = Math.max(1, Math.round(opts.ss ?? 4));
  const lineW = opts.lineWidthM ?? ROUTED_MAP_SPEC.line_width_m;
  const startEnd = opts.startEndM ?? ROUTED_MAP_SPEC.start_end_m;
  const minPath = opts.minPathM ?? ROUTED_MAP_SPEC.min_path_m;

  const W = size * ss;
  const r = (lineW / 2 / mpp) * ss;
  const ink = new Uint8Array(W * W);
  const half = size / 2;
  const s = Math.sin(pose.heading);
  const c = Math.cos(pose.heading);

  // World → continuous supersampled pixel. Robot frame: forward along the
  // heading, right perpendicular (Babylon: +x right, +z forward at heading 0;
  // heading grows from +z toward +x). Heading-up: forward is image up.
  const P = (wx: number, wz: number): [number, number] => {
    const dx = wx - pose.x;
    const dz = wz - pose.z;
    const fwd = dx * s + dz * c;
    const right = dx * c - dz * s;
    return [(half + right / mpp) * ss, (half - fwd / mpp) * ss];
  };
  const strokePolyline = (pts: readonly Vec2[], value: number, closed = false): void => {
    if (pts.length === 1) {
      const [x, y] = P(pts[0]!.x, pts[0]!.z);
      strokeSegment(ink, W, x, y, x, y, r, value);
      return;
    }
    const n = closed ? pts.length : pts.length - 1;
    for (let i = 0; i < n; i++) {
      const a = pts[i]!;
      const b = pts[(i + 1) % pts.length]!;
      const [ax, ay] = P(a.x, a.z);
      const [bx, by] = P(b.x, b.z);
      strokeSegment(ink, W, ax, ay, bx, by, r, value);
    }
  };

  // 1) Every way, white. Bands are OSM-like centreline polylines; a
  //    roundabout is a circular way at the ring's mid radius. Mouth flares
  //    (`frame: false`) only shape the drivable area — they are not ways.
  for (const piece of net.pieces) {
    if (piece.kind === "band") {
      if (piece.frame === false) continue;
      strokePolyline(piece.pts, INK_WHITE);
    } else {
      const rad = (piece.rIn + piece.rOut) / 2;
      const N = 72;
      const ring: Vec2[] = [];
      for (let i = 0; i < N; i++) {
        const a = (i / N) * 2 * Math.PI;
        ring.push({ x: piece.cx + rad * Math.sin(a), z: piece.cz + rad * Math.cos(a) });
      }
      strokePolyline(ring, INK_WHITE, true);
    }
  }

  // 2) The route: red over its whole length, then the green head and the
  //    blue tail on top. Sampled finely enough that the polyline's chords
  //    stay well inside the stroke on the tightest sim curves.
  const L = path.length;
  const step = Math.max(0.25, mpp / 2);
  const routePoints = (s0: number, s1: number): Vec2[] => {
    const out: Vec2[] = [];
    for (let d = s0; d < s1; d += step) {
      const p = path.point(d);
      out.push({ x: p.x, z: p.z });
    }
    const e = path.point(s1);
    out.push({ x: e.x, z: e.z });
    return out;
  };
  if (L > 0) {
    strokePolyline(routePoints(0, L), INK_RED);
    const headEnd = L < minPath ? L / 2 : Math.min(startEnd, L);
    const tailStart = L < minPath ? L / 2 : Math.max(0, L - startEnd);
    strokePolyline(routePoints(0, headEnd), INK_GREEN);
    strokePolyline(routePoints(tailStart, L), INK_BLUE);
  }

  // 3) Box-filter down to the output: the publisher's antialiased edges.
  const rgb = new Uint8Array(size * size * 3);
  const n = ss * ss;
  for (let v = 0; v < size; v++) {
    for (let u = 0; u < size; u++) {
      let rs = 0;
      let gs = 0;
      let bs = 0;
      for (let dy = 0; dy < ss; dy++) {
        const row = (v * ss + dy) * W + u * ss;
        for (let dx = 0; dx < ss; dx++) {
          const col = INK_RGB[ink[row + dx]!]!;
          rs += col[0];
          gs += col[1];
          bs += col[2];
        }
      }
      const i = (v * size + u) * 3;
      rgb[i] = Math.round(rs / n);
      rgb[i + 1] = Math.round(gs / n);
      rgb[i + 2] = Math.round(bs / n);
    }
  }

  return { size, mpp, rgb };
}

/** The real raster is OpenCV 8UC3 = BGR. Convert when writing it out that way. */
export function bgr(map: RoutedMap): Uint8Array {
  const out = new Uint8Array(map.rgb.length);
  for (let i = 0; i < map.rgb.length; i += 3) {
    out[i] = map.rgb[i + 2]!;
    out[i + 1] = map.rgb[i + 1]!;
    out[i + 2] = map.rgb[i]!;
  }
  return out;
}
